import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Mic,
  Calendar,
  Brain,
  Shield,
  Database,
  Users,
  ChevronDown,
  ExternalLink
} from 'lucide-react';

const CaseStudiesPage = () => {
  const [activeCase, setActiveCase] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  // Case study data
  const caseStudies = [
    {
      id: 1,
      title: 'Voice AI Recruiting System',
      category: 'AI System',
      icon: <Mic className="w-6 h-6" />,
      context: 'National sales organization with 100+ field representatives needed to scale candidate screening without adding headcount.',
      approach: 'Designed end-to-end AI recruiting system integrating voice AI, workflow automation, CRM, database, and scheduling. Built conversation flows, implemented post-call analysis using GPT-4, and optimized cost per interaction through prompt engineering and model selection.',
      architecture: 'Retell AI for voice conversations, n8n for workflow orchestration, Zoho Recruit for CRM, PostgreSQL for data storage, Google Calendar API for scheduling, GPT-4 for post-call analysis extracting 22 data points per conversation.',
      outcomes: [
        '700+ daily candidate interactions processed',
        'Cost per interaction reduced from $4 to under $2',
        '22 qualification and sentiment data points extracted per call',
        'System running in production with ongoing optimization'
      ]
    },
    {
      id: 2,
      title: 'Property Management Automation',
      category: 'Automation',
      icon: <Calendar className="w-6 h-6" />,
      context: 'Cleaning services company managing 100+ vacation rental properties was manually creating jobs from calendar bookings across Airbnb, VRBO, Booking.com, and other platforms.',
      approach: 'Built scheduling automation platform that ingests ICS calendar files, detects reservations vs. blocks, applies business logic for same-day turnovers and late checkouts, and automatically creates jobs in HousecallPro.',
      architecture: 'Custom MCP servers enabling natural language CRUD operations across Airtable and HousecallPro. Python automation scripts for calendar ingestion. Webhook integrations for real-time sync. Admin interface built in Airtable for non-technical staff.',
      outcomes: [
        '100+ properties automated',
        'Manual job creation eliminated',
        'Real-time sync between reservation systems and job scheduling',
        'Documentation and training delivered for ongoing operations'
      ]
    },
    {
      id: 3,
      title: 'Enterprise Authentication Modernization',
      category: 'Enterprise',
      icon: <Shield className="w-6 h-6" />,
      context: 'Major utility needed to modernize authentication for 13,000+ users while improving security posture.',
      approach: 'Led cross-functional engineering and security teams through Azure DevOps. Developed strategic roadmap that expanded program scope based on discovered opportunities. Implemented FIDO2-based passwordless authentication system.',
      architecture: null,
      outcomes: [
        'Program expanded from $500K to $1.3M through roadmap development',
        '13,000+ users impacted',
        'FIDO2 passwordless authentication implemented',
        'Cross-functional delivery across engineering and security teams'
      ]
    },
    {
      id: 4,
      title: 'Salesforce CRM Implementation',
      category: 'CRM',
      icon: <Database className="w-6 h-6" />,
      context: 'Tax services firm had three failed vendor attempts at Salesforce implementation. Sales leadership needed a platform that would actually drive adoption and improve conversion rates.',
      approach: 'Led discovery workshops to understand why previous attempts failed. Developed strategic roadmap focused on user adoption and business outcomes. Provided C-level coaching on Agile practices. Delivered implementation with change management focus.',
      architecture: null,
      outcomes: [
        'Sales conversion rates doubled',
        '$750K in change orders secured through roadmap expansion',
        'Successful delivery after three failed vendor attempts',
        'Executive coaching on Agile practices'
      ]
    },
    {
      id: 5,
      title: 'Fortune 500 CDP Strategy',
      category: 'Strategy',
      icon: <Users className="w-6 h-6" />,
      context: 'Global pharmaceutical company (Allergan/AbbVie) needed customer data platform strategy across multiple business units.',
      approach: 'Conducted 35+ hours of stakeholder interviews across 5-8 business units. Defined AI-driven customer data use cases. Influenced enterprise platform roadmap and capability requirements.',
      architecture: null,
      outcomes: [
        '35+ hours of executive stakeholder interviews',
        'AI-driven use case identification across business units',
        'Strategic influence on enterprise platform capabilities'
      ]
    },
    {
      id: 6,
      title: 'AI Enablement Program',
      category: 'Training',
      icon: <Brain className="w-6 h-6" />,
      context: 'Slalom consultants needed practical training on emerging AI tools and patterns to better serve clients exploring AI adoption.',
      approach: 'Selected as AI Navigator. Developed comprehensive GenAI training curriculum covering LLMs, responsible AI principles, and enterprise use cases. Created organizational AI readiness assessment framework with four maturity levels across Strategy, Governance, and Workforce dimensions.',
      architecture: null,
      outcomes: [
        'Training curriculum adopted across the firm',
        'AI readiness framework used in client discovery engagements',
        'Ongoing training on Copilot adoption, agentic patterns, and prompt engineering'
      ]
    }
  ];

  const toggleCaseStudy = (id) => {
    if (activeCase === id) {
      setActiveCase(null);
    } else {
      setActiveCase(id);
      setTimeout(() => {
        document.getElementById(`case-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 font-sans transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Header */}
      <div className="bg-gray-900 py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Selected Work
          </h1>
          <p className="text-xl text-gray-400">
            Technology transformation projects across enterprise and growth-stage companies
          </p>
        </div>
      </div>

      {/* Back to Home Link */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <Link
          to="/"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 font-medium transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Link>
      </div>

      {/* Case Studies List */}
      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className="space-y-4">
          {caseStudies.map((study) => (
            <div
              key={study.id}
              id={`case-${study.id}`}
              className={`bg-white rounded-lg overflow-hidden transition-all duration-300 border ${
                activeCase === study.id ? 'border-gray-300 shadow-md' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Case Study Header */}
              <div
                className="cursor-pointer"
                onClick={() => toggleCaseStudy(study.id)}
              >
                <div className="px-6 py-5 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 rounded-lg bg-gray-100 mr-4 text-gray-700">
                      {study.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {study.title}
                        </h3>
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {study.category}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm line-clamp-1">
                        {study.context}
                      </p>
                    </div>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform flex-shrink-0 ml-4 ${
                      activeCase === study.id ? 'transform rotate-180' : ''
                    }`}
                  />
                </div>
              </div>

              {/* Case Study Details */}
              {activeCase === study.id && (
                <div className="px-6 py-6 border-t border-gray-100 space-y-6 animate-fadeIn bg-gray-50">
                  {/* Context */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Context</h4>
                    <p className="text-gray-700">{study.context}</p>
                  </div>

                  {/* Approach */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Approach</h4>
                    <p className="text-gray-700">{study.approach}</p>
                  </div>

                  {/* Architecture */}
                  {study.architecture && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Architecture</h4>
                      <p className="text-gray-700">{study.architecture}</p>
                    </div>
                  )}

                  {/* Outcomes */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Outcomes</h4>
                    <ul className="space-y-2">
                      {study.outcomes.map((outcome, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-teal-600 mr-2 mt-1 flex-shrink-0">•</span>
                          <span className="text-gray-700">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact CTA */}
      <div className="bg-gray-900 py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Interested in similar results?
          </h2>
          <p className="text-lg text-gray-400 mb-8">
            Open to conversations about technology transformation, AI strategy, and implementation projects.
          </p>
          <a
            href={'mailto:' + ['BorisBlinderman', 'gmail.com'].join('@')}
            className="bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 rounded font-medium inline-flex items-center transition-colors"
          >
            Get in Touch
            <ExternalLink className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-950 text-white py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <span className="text-xl font-semibold">Boris Blinderman</span>
              <p className="text-gray-500 mt-1">From Roadmap to Production</p>
            </div>
            <div className="flex space-x-6">
              <a
                href="https://linkedin.com/in/BorisBlinderman"
                className="text-gray-500 hover:text-white transition-colors"
              >
                LinkedIn
              </a>
              <a
                href={'mailto:' + ['BorisBlinderman', 'gmail.com'].join('@')}
                className="text-gray-500 hover:text-white transition-colors"
              >
                Email
              </a>
              <a
                href="/Boris_Blinderman_Resume_2025.pdf"
                download
                className="text-gray-500 hover:text-white transition-colors"
              >
                Resume
              </a>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-600 text-sm">
            © {new Date().getFullYear()} Boris Blinderman
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default CaseStudiesPage;
